import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/features/auth/AuthContext";

import {
  criarWorkspace as criarNoServidor,
  listarWorkspaces,
  migrarWorkflowsLocaisUmaVez,
  removerWorkspace as removerNoServidor,
  renomearWorkspace as renomearNoServidor,
  type Workspace,
} from "./api";
import {
  buildWorkflow,
  loadLocalWorkflows,
  saveLocalWorkflows,
  type LocalWorkflow,
} from "./localWorkflows";

/**
 * A lista de setores, venha ela de onde vier.
 *
 * DUAS fontes, e a escolha não é preferência: com identidade ligada o setor
 * PRECISA existir no banco, porque toda rota com guarda confere se ele
 * pertence à organização de quem pede — um setor que só o navegador conhece
 * responde 404 em `/devices`, `/stream` e `/device-credentials`. Sem
 * identidade (bancada, API sem Postgres) não há organização dona de coisa
 * nenhuma, e aí o `localStorage` continua sendo a única fonte possível.
 *
 * Fundir os dois casos num só caminho foi tentador e está errado: a bancada
 * receberia 501 em toda carga, e a instalação com login continuaria criando
 * setores natimortos.
 */
export interface EstadoDosWorkspaces {
  workspaces: LocalWorkflow[];
  carregando: boolean;
  erro: string | null;
  /** Setores que subiram do `localStorage` para o banco nesta sessão. */
  migrados: number;
  /**
   * Conveniência de interface, não segurança: esconde o botão de criar para
   * quem receberia 403. Quem recusa de verdade é `autorizar()` no servidor.
   */
  podeCriar: boolean;
  /**
   * `true` quando a lista vem do servidor. Na bancada é `false`, e aí um id
   * que não está na lista continua sendo navegável — não há organização dona
   * de nada, então "este setor não existe" seria uma afirmação sem sentido.
   */
  fonteEhServidor: boolean;
  criar: (nome: string, descricao: string) => Promise<LocalWorkflow>;
  remover: (id: string) => Promise<void>;
  recarregar: () => void;
  renomear: (id: string, nome: string) => Promise<void>;
}

/**
 * Avisa as OUTRAS telas que a lista mudou.
 *
 * Cada tela tem sua própria instância do hook, então criar um setor na lista
 * não chegaria sozinho à barra lateral. Antes isso saía de graça porque
 * `saveLocalWorkflows` disparava o evento ao gravar; com a lista no servidor,
 * o aviso passa a ser explícito.
 */
function avisarOutrasTelas(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("ghm:workflows-updated"));
}

/** O formato do servidor, na forma que as telas já sabem desenhar. */
function paraWorkflow(workspace: Workspace): LocalWorkflow {
  return {
    id: workspace.id,
    name: workspace.nome,
    description: workspace.descricao ?? undefined,
    createdAt: workspace.criadaEm,
  };
}

export function useWorkspaces(): EstadoDosWorkspaces {
  const { aberto, permissoes, estado } = useAuth();
  const [workspaces, setWorkspaces] = useState<LocalWorkflow[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [migrados, setMigrados] = useState(0);
  const [versao, setVersao] = useState(0);

  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  useEffect(() => {
    // Enquanto o boot não respondeu não dá para saber qual fonte vale, e
    // chutar produziria uma lista que se contradiz um instante depois.
    if (estado === null) return;

    let cancelado = false;
    const controller = new AbortController();

    async function carregar() {
      setCarregando(true);
      setErro(null);

      if (aberto) {
        if (typeof window !== "undefined") {
          setWorkspaces(loadLocalWorkflows(window.localStorage));
        }
        setCarregando(false);
        return;
      }

      try {
        const doServidor = await listarWorkspaces(controller.signal);
        if (cancelado) return;

        // A migração roda só para quem pode criar. Um operador que abrisse a
        // tela primeiro receberia 403 em cada tentativa e sujaria o console
        // sem migrar nada — o admin migra na primeira visita dele.
        if (permissoes.gerenciarWorkspaces && typeof window !== "undefined") {
          const resultado = await migrarWorkflowsLocaisUmaVez(window.localStorage, doServidor);
          if (cancelado) return;
          if (resultado.migrados.length > 0) {
            setMigrados(resultado.migrados.length);
            setWorkspaces([...doServidor, ...resultado.migrados.filter((w) => !doServidor.some((known) => known.id === w.id))].map(paraWorkflow));
            setCarregando(false);
            return;
          }
        }

        setWorkspaces(doServidor.map(paraWorkflow));
      } catch (e) {
        if (cancelado) return;
        setErro(e instanceof Error ? e.message : "Falha ao carregar os setores.");
        // Falha transitoria nao remove os links conhecidos da navegacao.
      } finally {
        if (!cancelado) setCarregando(false);
      }
    }

    void carregar();
    return () => {
      cancelado = true;
      controller.abort();
    };
  }, [aberto, estado, permissoes.gerenciarWorkspaces, versao]);

  useEffect(() => {
    const refresh = () => recarregar();
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("ghm:workflows-updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("ghm:workflows-updated", refresh);
    };
  }, [recarregar]);

  const renomear = useCallback(async (id: string, nome: string) => {
    if (aberto) {
      const next = loadLocalWorkflows(window.localStorage).map((w) => w.id === id ? { ...w, name: nome.trim() } : w);
      saveLocalWorkflows(window.localStorage, next);
      setWorkspaces(next);
    } else {
      const saved = paraWorkflow(await renomearNoServidor(id, { nome }));
      setWorkspaces((current) => current.map((w) => w.id === id ? saved : w));
      avisarOutrasTelas();
    }
  }, [aberto]);

  const criar = useCallback(
    async (nome: string, descricao: string): Promise<LocalWorkflow> => {
      if (aberto) {
        const workflow = buildWorkflow(nome, descricao, workspaces);
        const proximo = [...workspaces, workflow];
        if (typeof window !== "undefined") {
          saveLocalWorkflows(window.localStorage, proximo);
        }
        setWorkspaces(proximo);
        return workflow;
      }

      // O id sai do SERVIDOR. Gerá-lo aqui e mandar junto faria o navegador
      // decidir identidade, e duas abas criando "Prensa" ao mesmo tempo
      // chegariam com o mesmo id — uma das duas perderia o setor para um
      // conflito que o servidor resolveria sozinho se pudesse numerar.
      const criado = paraWorkflow(
        await criarNoServidor({ nome, descricao: descricao.trim() || null }),
      );
      setWorkspaces((atual) => [...atual, criado]);
      avisarOutrasTelas();
      return criado;
    },
    [aberto, workspaces],
  );

  const remover = useCallback(
    async (id: string): Promise<void> => {
      if (aberto) {
        const proximo = loadLocalWorkflows(window.localStorage).filter((w) => w.id !== id);
        saveLocalWorkflows(window.localStorage, proximo);
        setWorkspaces(proximo);
        return;
      }
      await removerNoServidor(id);
      setWorkspaces((atual) => atual.filter((w) => w.id !== id));
      avisarOutrasTelas();
    },
    [aberto],
  );

  return {
    workspaces,
    carregando,
    erro,
    migrados,
    podeCriar: permissoes.gerenciarWorkspaces,
    fonteEhServidor: !aberto,
    criar,
    remover,
    recarregar,
    renomear,
  };
}
